import type { PersistConfig } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import sessionStorage from 'redux-persist/lib/storage/session';
import { sidebarWorkspace, StudioCacheState, StudioSession } from './studioCacheSlice';

export const STUDIO_RESUME_MAX_AGE_MS = 30 * 60_000;
const MAX_RESUME_BYTES = 2_000_000;
type Storage = Pick<typeof storage, 'getItem' | 'setItem' | 'removeItem'>;

function resumeSession(session: StudioSession): StudioSession | undefined {
  const workspace = session?.workspace;
  const project = workspace?.projects?.find(item => item.id === workspace.projectId);
  const chapter = project?.chapters?.find(item => item.id === workspace.chapterId);
  if (!chapter?.sequences?.some(item => item.id === chapter.activeSequenceId)
    || !chapter.conversations?.some(item => item.id === chapter.activeConversationId)
    || !session.updatedAt || Date.now() - session.updatedAt > STUDIO_RESUME_MAX_AGE_MS) return;
  // Keep the selected chapter's content and chat, with metadata for the rest of
  // the sidebar. Never persist the service capability, account balance or uploads.
  const preview = sidebarWorkspace(workspace);
  const activeProject = preview.projects.find(item => item.id === project!.id)!;
  activeProject.chapters = activeProject.chapters.map(item => item.id === chapter.id ? chapter : item);
  return { workspace: preview, updatedAt: session.updatedAt,
    pages: Object.fromEntries(Object.entries(session.pages || {}).filter(([key]) => key.startsWith(`${chapter.id}:`))) };
}

/** Keep private chapter content tab-local. The existing localStorage cache still
 * contains sidebar metadata only. Owner + backend must match before rehydrating. */
export function createStudioCacheStorage(local: Storage, tab: Storage): Storage {
  return {
    async getItem(key) {
      const persisted = await local.getItem(key);
      if (!persisted) return persisted;
      try {
        const base = JSON.parse(persisted);
        delete base.session;
        const resume = JSON.parse(await tab.getItem(`${key}:resume`) || 'null');
        if (resume && resume.ownerId === base.ownerId && resume.scope === base.scope) {
          const session = resumeSession(JSON.parse(resume.session));
          if (session) base.session = JSON.stringify(session);
        }
        return JSON.stringify(base);
      } catch { return persisted; }
    },
    async setItem(key, value) {
      const persisted = JSON.parse(value);
      const session = persisted.session;
      delete persisted.session;
      await local.setItem(key, JSON.stringify(persisted));
      try {
        const preview = session && resumeSession(JSON.parse(session));
        const resume = preview && JSON.stringify({ ownerId: persisted.ownerId, scope: persisted.scope,
          session: JSON.stringify(preview, (name, entry) => name === 'base64' ? undefined : entry) });
        if (resume && resume.length * 2 <= MAX_RESUME_BYTES) await tab.setItem(`${key}:resume`, resume);
        else await tab.removeItem(`${key}:resume`);
      } catch {
        // A full/disabled tab store must not break normal loading or keep an old view.
        await tab.removeItem(`${key}:resume`).catch(() => {});
      }
    },
    async removeItem(key) {
      await local.removeItem(key);
      await tab.removeItem(`${key}:resume`).catch(() => {});
    },
  };
}

export const studioCachePersistConfig: PersistConfig<StudioCacheState> = {
  key: 'studio-sidebar-v1', storage: createStudioCacheStorage(storage, sessionStorage), throttle: 1000,
  whitelist: ['ownerId', 'scope', 'data', 'session'],
};
